import { useState } from 'react';
import { useCreateUser, useRoles } from '@/hooks/useUsers';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { X } from 'lucide-react';

interface UserCreateDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function UserCreateDialog({ open, onOpenChange }: UserCreateDialogProps) {
  const [username, setPenggunaname] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [selectedRoles, setSelectedRoles] = useState<string[]>(['ROLE_USER']);
  const [isActive, setIsAktif] = useState(true);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const { data: rolesData } = useRoles();
  const createPengguna = useCreateUser();

  const validate = () => {
    const newErrors: Record<string, string> = {};
    if (!username.trim()) newErrors.username = 'Penggunaname is required';
    if (!email.trim()) newErrors.email = 'Email wajib diisi';
    if (!password.trim()) newErrors.password = 'Password is required';
    if (password.length < 6) newErrors.password = 'Password must be at least 6 characters';
    if (selectedRoles.length === 0) newErrors.roles = 'Minimal satu peran wajib dipilih';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    try {
      await createPengguna.mutateAsync({
        username,
        email,
        password,
        roles: selectedRoles,
        isActive,
      });
      onOpenChange(false);
      resetForm();
    } catch (error: any) {
      if (error.response?.data?.message) {
        setErrors({ submit: error.response.data.message });
      }
    }
  };

  const resetForm = () => {
    setPenggunaname('');
    setEmail('');
    setPassword('');
    setSelectedRoles(['ROLE_USER']);
    setIsAktif(true);
    setErrors({});
  };

  const toggleRole = (role: string) => {
    setSelectedRoles((prev) =>
      prev.includes(role)
        ? prev.filter((r) => r !== role)
        : [...prev, role]
    );
  };

  const removeRole = (role: string) => {
    setSelectedRoles((prev) => prev.filter((r) => r !== role));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Buat Pengguna Baru</DialogTitle>
          <DialogDescription>
            Create akun pengguna baru dengan peran dan izin tertentu.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          {errors.submit && (
            <div className="text-sm text-red-600 bg-red-50 p-2 rounded">
              {errors.submit}
            </div>
          )}
          <div className="space-y-2">
            <Label htmlFor="username">Penggunaname</Label>
            <Input
              id="username"
              value={username}
              onChange={(e) => setPenggunaname(e.target.value)}
              placeholder="Masukkan username"
            />
            {errors.username && (
              <p className="text-sm text-red-600">{errors.username}</p>
            )}
          </div>
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Masukkan email"
            />
            {errors.email && (
              <p className="text-sm text-red-600">{errors.email}</p>
            )}
          </div>
          <div className="space-y-2">
            <Label htmlFor="password">Password</Label>
            <Input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Masukkan kata sandi"
            />
            {errors.password && (
              <p className="text-sm text-red-600">{errors.password}</p>
            )}
          </div>
          <div className="space-y-2">
            <Label>Roles (IAM)</Label>
            <Select onValueChange={(value) => toggleRole(value)}>
              <SelectTrigger>
                <SelectValue placeholder="Pilih peran" />
              </SelectTrigger>
              <SelectContent>
                {rolesData?.data.map((role) => (
                  <SelectItem key={role.value} value={role.value}>
                    <div className="flex flex-col">
                      <span>{role.label}</span>
                      <span className="text-xs text-muted-foreground">
                        {role.description}
                      </span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.roles && (
              <p className="text-sm text-red-600">{errors.roles}</p>
            )}
            <div className="flex flex-wrap gap-2 mt-2">
              {selectedRoles.map((role) => {
                const roleInfo = rolesData?.data.find((r) => r.value === role);
                return (
                  <Badge key={role} variant="secondary" className="flex items-center gap-1">
                    {roleInfo?.label || role}
                    <button
                      type="button"
                      onClick={() => removeRole(role)}
                      className="ml-1 hover:text-red-600"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                );
              })}
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <Checkbox
              id="isActive"
              checked={isActive}
              onCheckedChange={(checked) => setIsAktif(checked as boolean)}
            />
            <Label htmlFor="isActive" className="text-sm font-normal">
              Pengguna aktif
            </Label>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Batal
            </Button>
            <Button type="submit" disabled={createPengguna.isPending}>
              {createPengguna.isPending ? 'Membuat...' : 'Buat Pengguna'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
